import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, Clock, AlertTriangle, FileText, Calendar, Activity, Wifi, WifiOff, AtSign, CheckCircle, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification type icons and colors
const notificationConfig = {
  authority_expiry: {
    icon: Clock,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    label: 'Authority Expiry'
  },
  filing_deadline: {
    icon: FileText,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    label: 'Filing Deadline'
  },
  activity: {
    icon: Activity,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    label: 'Activity'
  },
  system: {
    icon: AlertTriangle,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    label: 'System Alert'
  },
  command_center: {
    icon: Activity,
    color: 'text-[#2D7D6F]',
    bgColor: 'bg-[#2D7D6F]/10',
    label: 'Dashboard Update'
  },
  mention: {
    icon: AtSign,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    label: 'Mention'
  },
  approval_reminder: {
    icon: Clock,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    label: 'Approval Needed'
  },
  approval_escalation: {
    icon: AlertTriangle,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    label: 'Escalation'
  },
  section_approved: {
    icon: CheckCircle,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    label: 'Approved'
  },
  collaborator_added: {
    icon: Users,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    label: 'Team Update'
  }
};

// Single notification item
const NotificationItem = ({ notification, onDismiss, onAction }) => {
  // Support both 'category' and 'type' fields for notification config lookup
  const configKey = notification.category || notification.type || 'system';
  const config = notificationConfig[configKey] || notificationConfig.system;
  const Icon = config.icon;
  
  const priorityBorder = {
    high: 'border-l-4 border-l-red-500',
    medium: 'border-l-4 border-l-amber-500',
    low: 'border-l-2 border-l-blue-500'
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div 
      className={`p-3 hover:bg-muted/50 transition-colors cursor-pointer ${priorityBorder[notification.priority] || ''}`}
      onClick={() => onAction(notification.action_url, notification.id)}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${config.bgColor} shrink-0`}>
          <Icon className={`h-4 w-4 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-medium text-sm truncate">{notification.title}</p>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); onDismiss(notification.id); }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {notification.message}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              {config.label}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {formatTime(notification.timestamp)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'disconnected' | 'connecting' | 'connected'
  const [connectionType, setConnectionType] = useState(null); // 'websocket' | 'sse'
  const wsRef = useRef(null);
  const sseRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Connect to SSE (primary method - works through K8s ingress)
  const connectSSE = useCallback(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return;

    try {
      setConnectionStatus('connecting');
      const sseUrl = `${API_URL}/api/ws/notifications/sse?token=${token}`;
      const eventSource = new EventSource(sseUrl);
      sseRef.current = eventSource;

      eventSource.addEventListener('connection', (event) => {
        try {
          const data = JSON.parse(event.data);
          setConnectionStatus('connected');
          setConnectionType('sse');
        } catch (err) {
          console.error('SSE connection parse error:', err);
        }
      });

      eventSource.addEventListener('notification', (event) => {
        try {
          const data = JSON.parse(event.data);
          const newNotification = {
            id: `notif_${Date.now()}`,
            ...data,
            read: false
          };
          setNotifications(prev => [newNotification, ...prev.slice(0, 49)]);
          setUnreadCount(prev => prev + 1);
        } catch (err) {
          console.error('SSE notification parse error:', err);
        }
      });

      eventSource.addEventListener('ping', () => {
        // Keep-alive ping received
      });

      eventSource.onerror = (error) => {
        eventSource.close();
        sseRef.current = null;
        setConnectionStatus('disconnected');
        setConnectionType(null);
        
        // Retry after 5 seconds
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connectSSE();
        }, 5000);
      };

    } catch (err) {
      console.error('SSE connection failed:', err);
      setConnectionStatus('disconnected');
      
      // Retry after 5 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connectSSE();
      }, 5000);
    }
  }, []);

  // Connect to WebSocket (fallback, may not work in all K8s environments)
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return;

    // Build WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = API_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/notifications?token=${token}`;

    try {
      setConnectionStatus('connecting');
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionStatus('connected');
        setConnectionType('websocket');
        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'connection') {
          } else if (data.type === 'notification') {
            // Add new notification to the list
            const newNotification = {
              id: `notif_${Date.now()}`,
              ...data,
              read: false
            };
            setNotifications(prev => [newNotification, ...prev.slice(0, 49)]); // Keep last 50
            setUnreadCount(prev => prev + 1);
          } else if (data.type === 'pong') {
            // Heartbeat response
          }
        } catch (err) {
          console.error('WebSocket message parse error:', err);
        }
      };

      ws.onclose = (event) => {
        setConnectionStatus('disconnected');
        setConnectionType(null);
        wsRef.current = null;
        
        // If WebSocket fails with 403 (ingress blocking), fall back to SSE
        if (event.code === 1006 || event.code === 403) {
          connectSSE();
        } else if (event.code !== 1000) {
          // Reconnect after 5 seconds for other errors
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Fall back to SSE on error
        setConnectionStatus('disconnected');
        ws.close();
      };

      // Heartbeat ping every 30 seconds
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);

      // Cleanup ping on close
      ws.addEventListener('close', () => {
        clearInterval(pingInterval);
      });

    } catch (err) {
      console.error('WebSocket connection error:', err);
      // Fall back to SSE
      connectSSE();
    }
  }, [connectSSE]);

  // Disconnect all connections
  const disconnectAll = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setConnectionStatus('disconnected');
    setConnectionType(null);
  }, []);

  // Fetch stored notifications from API (mentions, approvals)
  const fetchStoredNotifications = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/tenders/notifications/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const storedNotifs = (data.notifications || []).map(n => ({
          id: n.notification_id,
          title: n.title,
          message: n.message,
          category: n.type, // Use 'type' field as category
          type: n.type,
          action_url: n.link,
          timestamp: n.created_at,
          read: n.read,
          priority: n.type === 'mention' ? 'medium' : 'low',
          data: n.data
        }));
        
        // Merge with existing notifications, avoiding duplicates
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const newNotifs = storedNotifs.filter(n => !existingIds.has(n.id));
          return [...newNotifs, ...prev].slice(0, 50);
        });
        
        setUnreadCount(prev => prev + data.unread_count);
      }
    } catch (err) {
      console.error('Failed to fetch stored notifications:', err);
    }
  }, []);

  // Connect on mount - try SSE first (more reliable through K8s)
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (token) {
      // Use SSE as primary (works through K8s ingress without special config)
      connectSSE();
      // Also fetch any stored notifications
      fetchStoredNotifications();
    }

    return () => {
      disconnectAll();
    };
  }, [connectSSE, disconnectAll, fetchStoredNotifications]);

  // Handle notification dismiss
  const handleDismiss = async (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Mark as read in backend if it's a stored notification
    const token = localStorage.getItem('session_token');
    if (token && notificationId.startsWith('ntf_')) {
      try {
        await fetch(`${API_URL}/api/tenders/notifications/${notificationId}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
  };

  // Handle notification action
  const handleAction = async (url, notificationId) => {
    // Mark as read when clicked
    if (notificationId) {
      await handleDismiss(notificationId);
    }
    if (url) {
      window.location.href = url;
    }
    setIsOpen(false);
  };

  // Mark all as read
  const markAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    
    // Mark all as read in backend
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        await fetch(`${API_URL}/api/tenders/notifications/mark-all-read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Failed to mark all notifications as read:', err);
      }
    }
  };

  // Clear all notifications
  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className={`h-5 w-5 ${connectionStatus === 'connected' ? 'text-foreground' : 'text-muted-foreground'}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {connectionStatus !== 'connected' && (
            <span 
              className={`absolute bottom-0 right-0 h-2 w-2 rounded-full ${connectionStatus === 'connecting' ? 'bg-amber-500 animate-pulse' : 'bg-red-500'}`} 
              title={connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'} 
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 p-0"
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {connectionStatus === 'connected' && (
              <span 
                className="h-2 w-2 rounded-full bg-[#2D7D6F]" 
                title={`Connected via ${connectionType === 'sse' ? 'SSE' : 'WebSocket'}`} 
              />
            )}
          </div>
          <div className="flex items-center gap-1">
            {notifications.length > 0 && (
              <>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead}>
                  <Check className="h-3 w-3 mr-1" />
                  Mark read
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearAll}>
                  Clear
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Notification List */}
        <div className="max-h-96 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onDismiss={handleDismiss}
                onAction={handleAction}
              />
            ))
          ) : (
            <div className="p-6 text-center">
              <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications</p>
              <p className="text-xs text-muted-foreground mt-1">
                {connectionStatus === 'connected' 
                  ? 'You\'re all caught up!' 
                  : connectionStatus === 'connecting' 
                    ? 'Connecting...' 
                    : 'Reconnecting...'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t">
            <Button 
              variant="ghost" 
              className="w-full text-xs h-8"
              onClick={() => { window.location.href = '/activity'; setIsOpen(false); }}
            >
              View all activity
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
